/**
 * HR PAYROLL — Basic Pay
 * ------------------------------------------------------------------
 * The dedicated home for each employee's basic salary, gratuity terms
 * and allowances — figures that previously had to be edited inside the
 * Deductions screen. It also surfaces the running "gratuity base to
 * date": the opening basic paid this contract plus the basic on every
 * payslip since, which is the amount gratuity is calculated on.
 *
 * Rendered two ways, both reusing BasicPayEditor:
 *   • BasicPayManager — school-wide tab in the Payroll Hub.
 *   • BasicPayPanel   — per-staff tab on the Staff Profile.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Wallet, Save, Loader2, Plus, Trash2, Search, ChevronRight } from 'lucide-react';
import { SoftCard, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { hr } from '@/features/hr/hrWrite';
import { useHrData } from '@/features/hr/useHrData';
import { useAuth } from '@/contexts/AuthContext';
import { recordHrAudit } from '@/lib/hrAudit';
import { staffFullName, type HrStaff } from '@/lib/hr';
import {
  round2,
  fmtPula,
  monthlyGratuityAccrual,
  gratuityAccruedOn,
  contractGratuityBasePaid,
  type StaffAllowance,
} from '@/lib/hrPayroll';

function numOf(v: string): number {
  const n = Number(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 block">{hint}</span>}
    </label>
  );
}

/* ==================================================================
   EDITOR — single staff member's basic pay + gratuity + allowances
   ================================================================== */
export function BasicPayEditor({ staff }: { staff: HrStaff }) {
  const data = useHrData();
  const { currentUser } = useAuth();
  const profile = data.payrollProfiles.find((p) => p.staffId === staff.id);

  const [basicSalary, setBasicSalary] = useState(profile ? String(profile.basicSalary) : '');
  const [salaryBand, setSalaryBand] = useState(profile?.salaryBand ?? '');
  const [gratuityRatePct, setGratuityRatePct] = useState(profile ? String(profile.gratuityRatePct) : '');
  const [gratuityEligible, setGratuityEligible] = useState(profile?.gratuityEligible ?? true);
  const [openingBasicPaid, setOpeningBasicPaid] = useState(
    profile?.openingBasicPaidThisContract != null ? String(profile.openingBasicPaidThisContract) : '',
  );
  const [allowances, setAllowances] = useState<StaffAllowance[]>(profile?.allowances ?? []);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const catalogLabels = data.allowanceCatalog.filter((a) => a.active).map((a) => a.label);

  const myContracts = useMemo(() => data.contracts.filter((c) => c.staffId === staff.id), [data.contracts, staff.id]);
  const activeContract = useMemo(() => myContracts.find((c) => c.active) ?? myContracts[0], [myContracts]);
  const myPayslips = useMemo(() => data.payslips.filter((p) => p.staffId === staff.id), [data.payslips, staff.id]);

  const basicNum = numOf(basicSalary);
  const rateNum = numOf(gratuityRatePct);
  const monthlyAccrual = monthlyGratuityAccrual(basicNum, rateNum);
  const now = new Date();
  // "Paid to date" — opening + basic on payslips already run this contract
  // (currentBasic 0: this month's slip isn't run yet on this screen).
  const baseToDate = contractGratuityBasePaid({
    openingBasicPaid: numOf(openingBasicPaid),
    contractStartISO: activeContract?.startDate,
    savedPayslips: myPayslips,
    currentPeriod: { month: now.getMonth() + 1, year: now.getFullYear() },
    currentBasic: 0,
    payouts: data.gratuityPayouts.filter((p) => p.staffId === staff.id && (!activeContract?.id || p.contractId === activeContract.id)),
  });
  const accruedToDate = gratuityAccruedOn(baseToDate, rateNum);

  function setAllowance(i: number, patch: Partial<StaffAllowance>) {
    setAllowances((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }
  function addAllowance() {
    setAllowances((prev) => [...prev, { type: '', amount: 0 }]);
  }
  function removeAllowance(i: number) {
    setAllowances((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function save() {
    setBusy(true);
    setErr('');
    setSaved(false);
    try {
      const cleanAllowances = allowances
        .map((a) => ({ type: a.type.trim(), amount: round2(Number(a.amount) || 0) }))
        .filter((a) => a.type && a.amount !== 0);
      const next = {
        staffId: staff.id,
        basicSalary: round2(basicNum),
        salaryBand: salaryBand.trim() || undefined,
        allowances: cleanAllowances,
        gratuityRatePct: round2(rateNum),
        gratuityEligible,
        openingBasicPaidThisContract: openingBasicPaid.trim() === '' ? undefined : round2(numOf(openingBasicPaid)),
        // Bank/payment account is edited on Payment Details — preserve it.
        ...(profile?.bank ? { bank: profile.bank } : {}),
      };
      await hr.payrollProfile.set(staff.id, next);
      await recordHrAudit({
        action: 'payroll.basic.update',
        actor: { id: currentUser?.uid ?? 'unknown', email: currentUser?.email ?? null },
        subjectId: staff.id,
        before: { basicSalary: profile?.basicSalary ?? null, gratuityRatePct: profile?.gratuityRatePct ?? null },
        after: { basicSalary: next.basicSalary, gratuityRatePct: next.gratuityRatePct },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <SoftCard hover={false}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <Wallet size={16} /> Basic pay &amp; gratuity — {staffFullName(staff)}
          </h3>
          <div className="flex items-center gap-3">
            {saved && <span className="text-xs text-emerald-600">Saved.</span>}
            {err && <span className="text-xs text-red-600">{err}</span>}
            <AccentButton icon={busy ? Loader2 : Save} disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save'}
            </AccentButton>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Basic salary (monthly, BWP)">
            <input
              type="number"
              inputMode="decimal"
              value={basicSalary}
              onChange={(e) => setBasicSalary(e.target.value)}
              placeholder="e.g. 12000"
              className={glassFieldClass('py-2')}
            />
          </Field>
          <Field label="Salary band (label only — drives no math)">
            <input value={salaryBand} onChange={(e) => setSalaryBand(e.target.value)} placeholder="e.g. C3" className={glassFieldClass('py-2')} />
          </Field>
          <Field label="Gratuity rate (%)">
            <input
              type="number"
              inputMode="decimal"
              value={gratuityRatePct}
              onChange={(e) => setGratuityRatePct(e.target.value)}
              placeholder="e.g. 25"
              className={glassFieldClass('py-2')}
            />
          </Field>
          <Field label="Gratuity eligible">
            <label className="flex items-center gap-2 py-2 text-sm text-gray-700 dark:text-gray-200">
              <input type="checkbox" checked={gratuityEligible} onChange={(e) => setGratuityEligible(e.target.checked)} />
              Accrues gratuity
            </label>
          </Field>
          <div className="lg:col-span-2">
            <Field
              label="Opening basic paid this contract (at go-live)"
              hint="Total basic already paid in the current contract before the system. Gratuity = rate × (this + basic paid since). Leave blank/0 for staff starting fresh."
            >
              <input
                type="number"
                inputMode="decimal"
                value={openingBasicPaid}
                onChange={(e) => setOpeningBasicPaid(e.target.value)}
                placeholder="0"
                className={glassFieldClass('py-2')}
              />
            </Field>
          </div>
        </div>

        {/* Allowances */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Allowances</h4>
            <button onClick={addAllowance} className="flex items-center gap-1 text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]">
              <Plus size={13} /> Add allowance
            </button>
          </div>
          {allowances.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No allowances. Basic salary only.</p>
          ) : (
            <div className="space-y-2">
              <datalist id={`bp-allow-${staff.id}`}>
                {catalogLabels.map((l) => <option key={l} value={l} />)}
              </datalist>
              {allowances.map((a, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    list={`bp-allow-${staff.id}`}
                    value={a.type}
                    onChange={(e) => {
                      const v = e.target.value;
                      const cat = data.allowanceCatalog.find((c) => c.active && c.label.toLowerCase() === v.toLowerCase());
                      if (cat && !a.amount && cat.defaultAmount != null) setAllowance(i, { type: v, amount: cat.defaultAmount });
                      else setAllowance(i, { type: v });
                    }}
                    placeholder="Allowance (pick or type, e.g. Housing)"
                    className={glassFieldClass('py-1.5 text-sm flex-1 min-w-0')}
                  />
                  <div className="w-32 shrink-0">
                    <input
                      type="number"
                      inputMode="decimal"
                      value={a.amount || ''}
                      onChange={(e) => setAllowance(i, { amount: Number(e.target.value) || 0 })}
                      placeholder="Amount"
                      className={glassFieldClass('py-1.5 text-sm text-right')}
                    />
                  </div>
                  <button onClick={() => removeAllowance(i)} className="text-gray-400 hover:text-red-500" title="Remove">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </SoftCard>

      {/* Balances to date — the gratuity base */}
      <SoftCard hover={false}>
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Balances to date</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <ToDate label="Opening (pre-system)" value={fmtPula(numOf(openingBasicPaid))} />
          <ToDate label="Basic paid since go-live" value={fmtPula(round2(Math.max(0, baseToDate - numOf(openingBasicPaid))))} />
          <ToDate label="Gratuity base to date" value={fmtPula(baseToDate)} strong />
          <ToDate label="Gratuity accrued to date" value={gratuityEligible ? fmtPula(accruedToDate) : '—'} />
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3">
          Monthly accrual at current basic &amp; rate: {gratuityEligible ? fmtPula(monthlyAccrual) : '—'}. Gratuity base resets when a new contract starts.
        </p>
        {gratuityEligible && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
            To pay out gratuity (partial or final), open this staff member&apos;s payslip in <span className="font-medium">Payroll → Workspace</span> and tick the gratuity payout option. Any payout reduces the base above and the unpaid balance carries forward.
          </p>
        )}
      </SoftCard>
    </div>
  );
}

function ToDate({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-xl bg-gray-50 dark:bg-gray-700/40 px-3 py-2.5">
      <span className="text-[11px] text-gray-500 dark:text-gray-400 block">{label}</span>
      <span className={`text-gray-800 dark:text-gray-100 ${strong ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  );
}

/* ==================================================================
   PROFILE PANEL — one staff member (Staff Profile → Basic Pay tab)
   ================================================================== */
export function BasicPayPanel({ staff }: { staff: HrStaff }) {
  return <BasicPayEditor key={staff.id} staff={staff} />;
}

/* ==================================================================
   HUB MANAGER — school-wide list + editor (Payroll Hub → Basic Pay)
   ================================================================== */
export function BasicPayManager({ onOpenStaff }: { onOpenStaff?: (id: string) => void }) {
  const data = useHrData();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.staff
      .filter((s) => s.status !== 'exited')
      .filter((s) => !q || `${s.firstName} ${s.lastName} ${s.employeeNo}`.toLowerCase().includes(q))
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }, [data.staff, search]);

  const selectedStaff = selected ? data.staff.find((s) => s.id === selected) ?? null : null;

  if (data.loading) return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[330px_1fr] gap-4">
      <SoftCard hover={false}>
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff…"
            className={glassFieldClass('py-2 pl-9')}
          />
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-auto">
          {rows.map((s) => {
            const profile = data.payrollProfiles.find((p) => p.staffId === s.id);
            const isSel = s.id === selected;
            return (
              <button
                key={s.id}
                onClick={() => setSelected(s.id)}
                className={`w-full text-left rounded-xl px-3 py-2 flex items-center justify-between gap-2 transition ${
                  isSel ? 'bg-[var(--accent-soft,#eef2fb)] dark:bg-gray-700/60' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{staffFullName(s)}</span>
                  <span className="block text-[11px] text-gray-400">#{s.employeeNo}</span>
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap flex items-center gap-1">
                  {fmtPula(profile?.basicSalary ?? 0)}
                  <ChevronRight size={14} className="text-gray-300" />
                </span>
              </button>
            );
          })}
          {rows.length === 0 && <p className="text-xs text-gray-500 px-3 py-2">No staff match.</p>}
        </div>
      </SoftCard>

      <div>
        {selectedStaff ? (
          <div className="space-y-2">
            {onOpenStaff && (
              <button
                onClick={() => onOpenStaff(selectedStaff.id)}
                className="text-xs font-medium text-[var(--accent-link,#2d5295)] hover:underline dark:text-[var(--accent-link-dark,#93c5fd)]"
              >
                Open full profile →
              </button>
            )}
            <BasicPayEditor key={selectedStaff.id} staff={selectedStaff} />
          </div>
        ) : (
          <SoftCard hover={false}>
            <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              <Wallet size={28} className="mx-auto mb-3 text-gray-300" />
              Select a staff member to view and edit their basic pay.
            </div>
          </SoftCard>
        )}
      </div>
    </div>
  );
}
